import { useEffect, useState, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, Play, Lock, Heart, Mail, User as UserIcon, ArrowRight, X, CheckCircle2 } from "lucide-react";
import { PageHeader, SectionLabel } from "@/components/shared";
import { Reveal, FadeUp } from "@/components/motion/Reveal";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { archiveCheckout, archiveVerify, getArchives, archiveStreamUrl, archiveMarkWatched } from "@/lib/api";
import { ASSETS } from "@/lib/assets";

const TOKEN_KEY = "wutn_archives_token";
const TOKEN_EXP_KEY = "wutn_archives_token_exp";
const AMOUNTS = [10, 25, 50, 100];

const isTokenValid = () => {
  const t = localStorage.getItem(TOKEN_KEY);
  const exp = localStorage.getItem(TOKEN_EXP_KEY);
  return !!(t && exp && new Date(exp) > new Date());
};
const fmtTime = (s) => {
  const m = Math.floor(s / 60); const sec = s % 60;
  return `${m}:${String(sec).padStart(2, "0")}`;
};

export default function Archives() {
  const [token, setToken] = useState(() => (isTokenValid() ? localStorage.getItem(TOKEN_KEY) : null));
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [active, setActive] = useState(null);
  const location = useLocation();
  const navigate = useNavigate();
  const videoRef = useRef(null);
  const verifying = useRef(false);

  // Handle Stripe checkout return (?session_id=...)
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const sid = params.get("session_id");
    if (sid && !verifying.current) {
      verifying.current = true;
      (async () => {
        try {
          const res = await archiveVerify(sid);
          if (res.payment_status === "paid" && res.token) {
            localStorage.setItem(TOKEN_KEY, res.token);
            localStorage.setItem(TOKEN_EXP_KEY, res.expires_at);
            setToken(res.token);
            toast.success("Thank you! Your partnership unlocked the archive.");
            navigate("/archives", { replace: true });
          } else {
            toast.error("Gift not yet confirmed. Please try again in a moment.");
          }
        } catch { toast.error("Could not verify your gift."); }
      })();
    }
  }, [location.search, navigate]);

  useEffect(() => {
    if (!token) return;
    setLoading(true);
    getArchives(token).then(setVideos).catch((err) => {
      if (err.response?.status === 401) {
        localStorage.removeItem(TOKEN_KEY); localStorage.removeItem(TOKEN_EXP_KEY); setToken(null);
      }
    }).finally(() => setLoading(false));
  }, [token]);

  const markWatched = (v) => {
    if (v.watched) return;
    archiveMarkWatched(v.id, token).catch(() => {});
    setVideos((prev) => prev.map((x) => x.id === v.id ? { ...x, watched: true } : x));
  };

  const openVideo = (v) => {
    setActive(v);
    if (v.source_type) markWatched(v);
  };

  if (!token) return <AccessGate />;

  return (
    <div data-testid="archives-page">
      <PageHeader
        testId="archives-header"
        label="Message Archives"
        title={<>Watch The <span className="text-wutn">Messages</span></>}
        subtitle="Full-length teaching and preaching from Kerby Stivene. Thank you for partnering with the mission — your gift keeps these messages free."
        image={ASSETS.preachingStage}
      />
      <section className="bg-black py-16 md:py-24">
        <div className="mx-auto max-w-[1400px] px-5 sm:px-8">
          <div className="flex items-center justify-between mb-10 flex-wrap gap-4">
            <SectionLabel>Library</SectionLabel>
            <button data-testid="archives-logout" onClick={() => { localStorage.removeItem(TOKEN_KEY); localStorage.removeItem(TOKEN_EXP_KEY); setToken(null); }} className="text-xs text-zinc-500 hover:text-wutn font-cond uppercase tracking-wider transition-colors">Sign out of archives</button>
          </div>
          {loading ? (
            <div className="py-24 flex justify-center"><div className="h-10 w-10 border-2 border-wutn border-t-transparent rounded-full animate-spin" /></div>
          ) : videos.length === 0 ? (
            <div className="py-20 text-center"><p className="text-zinc-500">No videos uploaded yet. Check back soon.</p></div>
          ) : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-8">
              {videos.map((v, i) => (
                <FadeUp key={v.id} delay={(i % 3) * 0.05}>
                  <article data-testid={`archive-card-${v.id}`} className="group">
                    <button onClick={() => openVideo(v)} className="block w-full relative aspect-video bg-zinc-900 overflow-hidden border border-white/10 group-hover:border-wutn transition-colors" data-testid={`archive-play-${v.id}`}>
                      {v.thumbnail_url ? (
                        <img src={v.thumbnail_url} alt={v.title} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-black to-[#1a0000]"><span className="font-display uppercase text-6xl text-outline-red">WUTN</span></div>
                      )}
                      <div className="absolute inset-0 bg-black/30 group-hover:bg-black/50 transition-colors flex items-center justify-center">
                        <span className="flex items-center justify-center h-16 w-16 bg-wutn text-white transition-transform duration-300 group-hover:scale-110"><Play className="h-7 w-7 fill-white ml-1" /></span>
                      </div>
                      {v.watched && (
                        <div className="absolute top-2 left-2 flex items-center gap-1.5 bg-black/80 text-wutn px-2 py-1 text-[10px] uppercase tracking-widest font-cond"><CheckCircle2 className="h-3 w-3" /> Watched</div>
                      )}
                    </button>
                    <h3 className="mt-4 font-cond uppercase text-xl leading-tight group-hover:text-wutn transition-colors">{v.title}</h3>
                    {v.description && <p className="mt-2 text-sm text-zinc-500 line-clamp-2">{v.description}</p>}
                  </article>
                </FadeUp>
              ))}
            </div>
          )}
        </div>
      </section>

      <Dialog open={!!active} onOpenChange={(v) => { if (!v) setActive(null); }}>
        <DialogContent className="max-w-5xl w-[95vw] p-0 bg-black border border-white/10 rounded-none max-h-[92vh] overflow-y-auto">
          {active && (
            <div>
              <div className="flex items-center justify-between px-5 py-3 border-b border-white/10 sticky top-0 bg-black z-10">
                <h3 className="font-cond uppercase text-white truncate">{active.title}</h3>
                <button onClick={() => setActive(null)} className="p-1.5 text-zinc-400 hover:text-wutn" aria-label="Close"><X className="h-5 w-5" /></button>
              </div>
              {active.source_type === "youtube" ? (
                <iframe className="w-full aspect-video max-h-[70vh] bg-black" src={`https://www.youtube.com/embed/${active.source_id}?autoplay=1&rel=0`} title={active.title} allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen data-testid={`archive-video-${active.id}`} />
              ) : active.source_type === "gdrive" ? (
                <iframe className="w-full aspect-video max-h-[70vh] bg-black" src={`https://drive.google.com/file/d/${active.source_id}/preview`} title={active.title} allow="autoplay; encrypted-media; fullscreen" allowFullScreen data-testid={`archive-video-${active.id}`} />
              ) : (
                <video ref={videoRef} controls autoPlay onPlay={() => markWatched(active)} className="w-full max-h-[70vh] bg-black" src={archiveStreamUrl(active.id, token)} data-testid={`archive-video-${active.id}`} />
              )}
              {!active.source_type && active.chapters && active.chapters.length > 0 && (
                <div className="px-5 py-4 border-t border-white/10">
                  <div className="text-xs uppercase tracking-widest text-wutn font-bold mb-3">Chapters</div>
                  <ol className="space-y-1">
                    {active.chapters.map((c, i) => (
                      <li key={i}>
                        <button
                          onClick={() => { if (videoRef.current) { videoRef.current.currentTime = c.time_seconds; videoRef.current.play(); } }}
                          data-testid={`chapter-${i}`}
                          className="w-full flex items-baseline gap-3 py-1.5 text-left text-sm text-zinc-300 hover:text-wutn transition-colors"
                        >
                          <span className="font-mono text-wutn text-xs w-12 shrink-0">{fmtTime(c.time_seconds)}</span>
                          <span>{c.title}</span>
                        </button>
                      </li>
                    ))}
                  </ol>
                </div>
              )}
              {active.description && <p className="px-5 py-4 text-sm text-zinc-400 border-t border-white/10">{active.description}</p>}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function AccessGate() {
  const [form, setForm] = useState({ name: "", email: "", amount: 25, custom: "" });
  const [loading, setLoading] = useState(false);
  const location = useLocation();
  const canceled = new URLSearchParams(location.search).get("canceled");

  useEffect(() => {
    if (canceled) toast.message("Checkout canceled. Your partnership was not completed.");
  }, [canceled]);

  const pickAmount = (a) => setForm({ ...form, amount: a, custom: "" });
  const effectiveAmount = form.custom ? parseFloat(form.custom) || 0 : form.amount;

  const checkout = async (e) => {
    e.preventDefault();
    if (!form.email || !effectiveAmount || effectiveAmount < 1) return toast.error("Enter your email and a gift amount");
    setLoading(true);
    try {
      const res = await archiveCheckout({ email: form.email, name: form.name, amount: effectiveAmount, origin_url: window.location.origin });
      window.location.href = res.checkout_url;
    } catch (err) { toast.error(err.response?.data?.detail || "Could not start checkout"); setLoading(false); }
  };

  const inputCls = "w-full bg-transparent border-b border-white/20 focus:border-wutn py-3 text-white placeholder:text-zinc-500 outline-none transition-colors";

  return (
    <div data-testid="archives-gate">
      <PageHeader testId="archives-gate-header" label="Message Archives" title={<>Partner To <span className="text-wutn">Unlock</span></>} subtitle="Access to the full library of Kerby Stivene's messages opens with a one-time gift of any size. Your gift keeps every message free for the next person." image={ASSETS.preachingStage} />

      <section className="bg-black py-16 md:py-24">
        <div className="mx-auto max-w-[1400px] px-5 sm:px-8 grid lg:grid-cols-12 gap-14">
          <div className="lg:col-span-5">
            <SectionLabel>Partner & Unlock</SectionLabel>
            <h2 className="mt-5 font-display uppercase text-4xl md:text-5xl leading-[0.9]">A Gift Of <span className="text-wutn">Any Size</span></h2>
            <p className="mt-6 text-zinc-400 text-lg">We don't gatekeep the Gospel — but hosting, editing and distributing full-length messages isn't free. Give what the Lord puts on your heart and we'll unlock the library instantly after your gift.</p>
            <div className="mt-10 flex items-start gap-3 text-xs text-zinc-500">
              <Lock className="h-4 w-4 text-wutn shrink-0 mt-0.5" />
              <span>Your gift is securely processed by Stripe. Access remains available on this device for 30 days.</span>
            </div>
          </div>

          <div className="lg:col-span-6 lg:col-start-7">
            <Reveal>
              <form onSubmit={checkout} data-testid="archives-checkout-form" className="bg-[#0A0A0A] border border-white/10 p-8 md:p-10 space-y-7">
                <div>
                  <h3 className="font-display uppercase text-3xl">Give & <span className="text-wutn">Unlock</span></h3>
                  <p className="mt-2 text-zinc-400 text-sm">You'll be sent to Stripe to complete your gift. On return, the library opens automatically.</p>
                </div>
                <div>
                  <label className="text-xs uppercase tracking-widest text-zinc-500 flex items-center gap-2"><UserIcon className="h-3 w-3" /> Your Name</label>
                  <input value={form.name} onChange={(e)=>setForm({...form,name:e.target.value})} data-testid="archives-name" className={inputCls} placeholder="(optional)" />
                </div>
                <div>
                  <label className="text-xs uppercase tracking-widest text-zinc-500 flex items-center gap-2"><Mail className="h-3 w-3" /> Email *</label>
                  <input type="email" required value={form.email} onChange={(e)=>setForm({...form,email:e.target.value})} data-testid="archives-email" className={inputCls} placeholder="you@example.com" />
                </div>
                <div>
                  <label className="text-xs uppercase tracking-widest text-zinc-500 mb-3 block">Gift Amount (CAD) *</label>
                  <div className="flex flex-wrap gap-2 mb-3">
                    {AMOUNTS.map((a) => (
                      <button type="button" key={a} onClick={()=>pickAmount(a)} data-testid={`archives-amount-${a}`} className={`px-5 py-2.5 font-cond uppercase tracking-wider text-sm border transition-colors ${(!form.custom && form.amount===a) ? "bg-wutn border-wutn text-white" : "border-white/20 text-zinc-400 hover:border-wutn hover:text-white"}`}>${a}</button>
                    ))}
                  </div>
                  <input type="number" min="1" step="1" value={form.custom} onChange={(e)=>setForm({...form,custom:e.target.value})} data-testid="archives-custom-amount" className={inputCls} placeholder="Or type any amount" />
                </div>
                <button type="submit" disabled={loading} data-testid="archives-checkout-btn" className="w-full inline-flex items-center justify-center gap-2 bg-wutn hover:bg-wutn-dark disabled:opacity-60 text-white px-6 py-5 font-display uppercase text-2xl transition-colors">
                  {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : <Heart className="h-5 w-5" />} Give ${effectiveAmount || 0} & Unlock <ArrowRight className="h-5 w-5" />
                </button>
              </form>
            </Reveal>
          </div>
        </div>
      </section>
    </div>
  );
}
